import { debugRotateOffers, debugSpecificProducts } from '../../../helpers/debug';
import { SHContent, SHDecision, SHMetadata, SHOffer } from '../types';

export function getOffersFromDecide(decisionList?: SHDecision[]) {
  try {
    if (!decisionList || decisionList?.length < 1) {
      return [];
    }

    const formattedDecisions: SHOffer[] = [];
    for (const decision of decisionList) {
      if (!decision?.metadata?.content?.title) continue;

      const metadata: Partial<SHMetadata> = { ...decision.metadata };
      const content: SHContent = { ...decision.metadata.content };
      delete metadata.content;

      const formattedDecision: SHOffer = {
        generalInfo: {
          annuity: decision?.annuity,
          category: decision?.category,
          id: decision?.id,
          partner: decision?.partner,
          slug: decision?.slug,
          featured: decision?.featured,
          limit: decision?.limit,
          lowLimit: decision?.lowLimit,
          preApproved: decision?.preApproved,
          value: decision?.value,
          modal: decision?.modal,
          clicked: decision?.clicked || 0,
          viewed: decision?.viewed || 0,
          rotationLimit: decision?.rotation_limit || 30,
        },
        metadata,
        content,
      };

      formattedDecisions.push(getLimitMessage(formattedDecision));
    }

    debugSpecificProducts({
      productList: formattedDecisions,
      origin: 'DECIDE',
    });

    const rotatedOffers = rotateOffers(formattedDecisions);
    debugRotateOffers({ formattedDecisions, rotatedOffers });

    return rotatedOffers;
  } catch (error) {
    console.error('[SH] Error getting offers from decide:', error);
    return [];
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getOffersFromStoryblok(storyblokContent: any) {
  try {
    if (storyblokContent?.stories?.length < 1) {
      return [];
    }

    const formattedOffers: SHOffer[] = [];
    for (const story of storyblokContent.stories) {
      const offerContent = story?.content;
      if (!story?.slug || !offerContent?.title) continue;

      const offer: SHOffer = {
        generalInfo: {
          category: offerContent?.category,
          id: offerContent?.id,
          partner: offerContent?.partner,
          slug: offerContent?.slug,
          featured: offerContent?.featured,
          limit: offerContent?.limit,
          limitMessage: offerContent?.limitMessage,
          lowLimit: offerContent?.lowLimit,
          preApproved: offerContent?.preApproved,
          value: offerContent?.value,
          modal: offerContent?.modal,
          clicked: offerContent?.clicked || 0,
          viewed: offerContent?.viewed || 0,
          rotationLimit: offerContent?.rotation_limit || 30,
        },
        metadata: {
          alternates: offerContent?.alternates,
          id: offerContent?.id,
          is_startpage: offerContent?.is_startpage,
          name: offerContent?.name,
          path: offerContent?.path,
          position: offerContent?.position,
          slug: offerContent?.slug,
          uuid: offerContent?.uuid,
        },
        content: {
          _uid: offerContent?._uid,
          name: offerContent?.name,
          slug: offerContent?.slug,
          links: offerContent?.links,
          title: offerContent?.title,
          title_highlight: offerContent?.title_highlight,
          partner: offerContent?.partner,
          partner_friendly_name: offerContent?.partner_friendly_name,
          variant: offerContent?.variant,
          category: offerContent?.category,
          benefit_category: offerContent?.benefit_category,
          subtitle: offerContent?.subtitle,
          component: offerContent?.component,
          default_limit: offerContent?.default_limit,
          // TODO: remover image_logo ao migrar todos os componentes para o campo image
          image_logo: {
            filename: offerContent?.image_logo?.filename,
            alt: offerContent?.image_logo?.alt,
          },
          image: offerContent?.image?.filename
            ? { filename: offerContent.image.filename, alt: offerContent.image.alt }
            : undefined,
          is_card_image: offerContent?.is_card_image,
          button_text: offerContent?.button_text,
          description: offerContent?.description,
          full_funnel: offerContent?.full_funnel,
          highlight_card: offerContent?.highlight_card,
          highlight_details: offerContent?.highlight_details,
          highlight_marketplace: offerContent?.highlight_marketplace,
          feature_information: offerContent?.feature_information,
          subject_to_credit_approval: offerContent?.subject_to_credit_approval,
          benefits: offerContent?.benefits,
          detail_page: offerContent?.detail_page,
          limit: offerContent?.limit,
          preApproved: offerContent?.pre_approved,
          microcopy: offerContent?.microcopy,
        },
      };

      formattedOffers.push(offer);
    }

    return formattedOffers;
  } catch (error) {
    console.error('[SH] Error getting offers from storyblok:', error);
    return [];
  }
}

function rotateOffers(offersList: SHOffer[]) {
  const priorityList: SHOffer[] = [];
  const normalList: SHOffer[] = [];

  offersList.forEach((offer) => {
    const { viewed, rotationLimit } = offer.generalInfo;

    if (priorityList.length >= 2) {
      return normalList.push(offer);
    }

    if (viewed <= rotationLimit) {
      return priorityList.push(offer);
    }

    normalList.push(offer);
  });

  return [...priorityList, ...normalList];
}

function getLimitMessage(offer: SHOffer) {
  const hasLimit = Boolean(offer?.generalInfo?.limit && offer?.generalInfo?.limit > 0);
  const hasDefaultLimit = Boolean(offer?.content?.default_limit);
  const hasLowLimit = offer?.generalInfo?.lowLimit || offer?.generalInfo?.lowLimit === undefined;

  if (!hasLimit && !hasDefaultLimit) {
    return offer;
  }

  const safeLimit = offer?.generalInfo?.limit || 0;
  const limitInCurrency = `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safeLimit)}`;

  const isCardNeon = Boolean(offer?.generalInfo?.slug?.toLowerCase().includes('cards-neon'));
  const messageComposeLimit = isCardNeon ? 'de' : hasLowLimit ? 'até' : 'de';
  const messageComposeType = offer.content?.category === 'cards' ? `Limite` : 'Libera';

  const dynamicLimitMessage = `${messageComposeType} ${messageComposeLimit} R$${limitInCurrency}`;
  const defaultMessage = hasDefaultLimit ? `${messageComposeType} ${messageComposeLimit} R$${offer?.content?.default_limit}` : '';

  // lowLimit true => prioriza o limite do Storyblok; sem default, cai no limite do backend
  // lowLimit false/undefined => prioriza o limite do backend; sem ele, cai no Storyblok
  const isLowLimit = offer?.generalInfo?.lowLimit === true;
  const shouldUseDefault = isLowLimit ? hasDefaultLimit : safeLimit <= 0;
  const limitMessage = shouldUseDefault ? defaultMessage : dynamicLimitMessage;

  return {
    ...offer,
    generalInfo: {
      ...offer.generalInfo,
      limitMessage,
    },
  } as SHOffer;
}
